import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";

/**
 * Shared paginator used by Clients and Prospects lists.
 * Page-size options: 25 / 50 / 100 / all. When "all" is selected, the pager
 * hides and the label switches to "Menampilkan semua N".
 *
 * Props:
 *   total, page, totalPages, pageSize (string "25"|"50"|"100"|"all"), startIdx, endIdx,
 *   onPageSizeChange, onPageChange, label (singular, default "client"), testidPrefix (default "pagination")
 */
export default function Pagination({
  total,
  page,
  totalPages,
  pageSize,
  startIdx,
  endIdx,
  onPageSizeChange,
  onPageChange,
  label = "item",
  testidPrefix = "pagination",
}) {
  const isAll = pageSize === "all";
  // Compact numbering: 1 … x-1 x x+1 … N
  const numbers = [];
  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i++) numbers.push(i);
  } else {
    const push = (n) => { if (n >= 1 && n <= totalPages && !numbers.includes(n)) numbers.push(n); };
    push(1);
    if (page > 3) numbers.push("…");
    push(page - 1); push(page); push(page + 1);
    if (page < totalPages - 2) numbers.push("…");
    push(totalPages);
  }
  return (
    <div className="flex flex-col md:flex-row items-center justify-between gap-3 pt-1" data-testid={`${testidPrefix}-bar`}>
      <div className="flex items-center gap-2 text-xs text-[#626061]">
        <span className="text-[10px] uppercase tracking-widest text-[#A0A3A8] font-semibold">Tampilkan</span>
        <Select value={pageSize} onValueChange={onPageSizeChange}>
          <SelectTrigger data-testid={`${testidPrefix}-page-size`} className="h-8 w-24 rounded-full bg-white card-soft border-transparent px-3 text-xs font-semibold">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="25">25</SelectItem>
            <SelectItem value="50">50</SelectItem>
            <SelectItem value="100">100</SelectItem>
            <SelectItem value="all">Semua</SelectItem>
          </SelectContent>
        </Select>
        <span className="tabular-nums">
          {isAll ? (
            <>Menampilkan <b className="text-[#090808]">semua {total}</b> {label}</>
          ) : (
            <>Menampilkan <b className="text-[#090808]">{total === 0 ? 0 : startIdx + 1}–{endIdx}</b> dari <b className="text-[#090808]">{total}</b> {label}</>
          )}
        </span>
      </div>
      {!isAll && totalPages > 1 && (
        <div className="flex items-center gap-1" data-testid={`${testidPrefix}-pager`}>
          <PagerBtn onClick={() => onPageChange(1)} disabled={page === 1} testid={`${testidPrefix}-first`} title="Halaman pertama"><ChevronsLeft className="w-4 h-4" /></PagerBtn>
          <PagerBtn onClick={() => onPageChange(page - 1)} disabled={page === 1} testid={`${testidPrefix}-prev`} title="Sebelumnya"><ChevronLeft className="w-4 h-4" /></PagerBtn>
          {numbers.map((n, i) =>
            n === "…" ? (
              <span key={`e${i}`} className="px-2 text-[#A0A3A8] text-xs select-none">…</span>
            ) : (
              <button
                key={n}
                onClick={() => onPageChange(n)}
                data-testid={`${testidPrefix}-page-${n}`}
                aria-current={n === page ? "page" : undefined}
                className={`h-8 min-w-[32px] px-2 rounded-full text-xs font-bold tabular-nums transition-colors ${
                  n === page ? "bg-[#090808] text-white shadow-[0_4px_10px_rgba(9,8,8,0.20)]" : "text-[#626061] hover:bg-[#F5F3FF]"
                }`}
              >{n}</button>
            )
          )}
          <PagerBtn onClick={() => onPageChange(page + 1)} disabled={page >= totalPages} testid={`${testidPrefix}-next`} title="Berikutnya"><ChevronRight className="w-4 h-4" /></PagerBtn>
          <PagerBtn onClick={() => onPageChange(totalPages)} disabled={page >= totalPages} testid={`${testidPrefix}-last`} title="Halaman terakhir"><ChevronsRight className="w-4 h-4" /></PagerBtn>
        </div>
      )}
    </div>
  );
}

function PagerBtn({ children, onClick, disabled, testid, title }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      title={title}
      className="h-8 w-8 inline-flex items-center justify-center rounded-full text-[#626061] hover:bg-[#F5F3FF] disabled:opacity-40 disabled:hover:bg-transparent transition-colors"
    >
      {children}
    </button>
  );
}

/**
 * Small helper: given a filtered array + pageSize state, produce { paged, totalPages, startIdx, endIdx }.
 * Consumer still owns the page state so a filter change can reset via useEffect.
 */
export function paginate(filtered, pageSize, page) {
  const isAll = pageSize === "all";
  const perPage = isAll ? filtered.length || 1 : Number(pageSize);
  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const curPage = Math.min(page, totalPages);
  const startIdx = (curPage - 1) * perPage;
  const endIdx = isAll ? filtered.length : Math.min(startIdx + perPage, filtered.length);
  const paged = isAll ? filtered : filtered.slice(startIdx, startIdx + perPage);
  return { paged, totalPages, curPage, startIdx, endIdx };
}
